/**
 * The navigation, filtered by release and then by entitlement.
 *
 * WAS TWELVE, IS NOW ELEVEN, AND NEITHER NUMBER IS THE POINT. The order, the
 * labels and the icons are design (v1.0 §7 and §16), and nothing here invents
 * an entry or reorders one. What changed is the approved commercial model:
 * Procurement and Compliance & Risk are Phase 1 modules and Field Service, LMS
 * and AI & Automation are not, so the table below carries all fourteen and
 * `navigationFor` shows the eleven the model offers.
 *
 * An entry is filtered TWICE and the two filters answer different questions.
 * `commercialRelease` asks whether the product offers this module at all;
 * entitlement asks whether this tenant bought it. Conflating them would mean
 * either selling something outside the approved model or hiding something a
 * tenant is paying for.
 *
 * PAGES ARE NO LONGER A PLACEHOLDER. Stage 2 gave every module the single
 * generic landing page, because inventing a page list would have been
 * inventing a product. Stages 5 to 8 then built real routes, and the
 * placeholder stayed — so the sidebar could not express any of them and each
 * module's landing page had to carry its whole information architecture as a
 * wall of buttons. `PAGE_MAP` below is that list, for the routes that exist.
 */
import { MODULE_ICONS, type ShellModule } from '@tessma/design-system';
import { reachableNavigationModules } from '../session/guards.js';
import type { SessionState } from '../session/session.js';

interface FrozenNavigationEntry {
  readonly id: string;
  readonly label: string;
  readonly icon: ShellModule['icon'];
}

/**
 * Every navigation entry the design defines, in order.
 *
 * The three entries outside the approved Phase 1 model stay in this table on
 * purpose. Their manifests are still real — `finance.optionalModules` names
 * Field Service — so deleting the row would only move the omission somewhere
 * less visible. `navigationFor` filters them out by release; the labels and
 * icons wait here for the release that offers them.
 */
export const FROZEN_NAVIGATION: readonly FrozenNavigationEntry[] = Object.freeze([
  { id: 'dashboard', label: 'Dashboard', icon: MODULE_ICONS.dashboard },
  { id: 'finance', label: 'Finance', icon: MODULE_ICONS.finance },
  { id: 'crm', label: 'CRM', icon: MODULE_ICONS.crm },
  { id: 'procurement', label: 'Procurement', icon: MODULE_ICONS.procurement },
  { id: 'field-service', label: 'Field Service', icon: MODULE_ICONS.field },
  { id: 'service-support', label: 'Service & Support', icon: MODULE_ICONS.service },
  { id: 'hr', label: 'HR', icon: MODULE_ICONS.hr },
  { id: 'projects', label: 'Projects', icon: MODULE_ICONS.projects },
  // "Documents & Contracts Management" is the module's approved name and is
  // what the catalogue carries. The sidebar shows the shorter form: a label
  // that wraps to three lines in a 240px rail is a worse answer than a name
  // everybody already uses for it.
  { id: 'documents', label: 'Documents & Contracts', icon: MODULE_ICONS.documents },
  { id: 'compliance', label: 'Compliance & Risk', icon: MODULE_ICONS.compliance },
  { id: 'lms', label: 'LMS', icon: MODULE_ICONS.lms },
  { id: 'ai-automation', label: 'AI & Automation', icon: MODULE_ICONS.ai },
  // Renamed with the module. This is the BI SKU, not global Ask TESSMA AI and
  // not the later AI & Automation module.
  { id: 'reports', label: 'Intelligence', icon: MODULE_ICONS.reports },
  { id: 'administration', label: 'Administration', icon: MODULE_ICONS.admin },
]);

/** The landing page every module has, and the fallback when a route has none. */
export const LANDING_PAGE = 'Overview';

/**
 * WHERE A ROUTE IS, IN THE PRODUCT'S OWN WORDS.
 *
 * ONE TABLE, TWO CONSUMERS, WHICH IS THE POINT. The shell's breadcrumb and
 * title read it, and the sidebar's sub-navigation is built from it. Two lists
 * would be two answers to "what page is this", and the first time they
 * disagreed the wrong one is the one somebody would be looking at.
 *
 * WHAT WAS WRONG. `AuthenticatedLayout` passed the literal string 'Overview'
 * as the active page for every route in the product, so /finance/invoices
 * announced itself as "Finance › Overview" with a page title of "Overview".
 * Nothing was mis-styled; the shell simply had no idea where you were.
 *
 * THE LABELS ARE THE BUSINESS'S, NOT THE SCHEMA'S. "Receivables ageing", not
 * `finance_ageing`; "Customers", not "Party Master".
 *
 * `sidebar: false` keeps a route addressable — with a correct breadcrumb and
 * title — without listing it in the sidebar. Detail routes and create forms
 * are reached from their list, and a navigation entry for "New sales invoice"
 * would be an action in a list of places.
 */
export interface PageEntry {
  /** Exact path, or a prefix ending in `/` for a parameterised route. */
  readonly path: string;
  readonly moduleId: string;
  readonly page: string;
  /** Offered in the sidebar only when the session holds this permission. */
  readonly permission?: string;
  readonly sidebar?: boolean;
  /**
   * One line under the `<h1>` saying what this page is for.
   *
   * From the same table as the breadcrumb and the title, so the three cannot
   * disagree. Optional, and a page without one renders exactly as it did.
   */
  readonly description?: string;
  /**
   * The `<h1>`, where the frozen design's heading is not the page's own name.
   *
   * The Main Dashboard is "Overview" in the breadcrumb and the sidebar, and
   * "Dashboard Overview" as the page's heading — which is what the frozen
   * reference draws. One table still answers all three.
   */
  readonly title?: string;
}

export const PAGE_MAP: readonly PageEntry[] = Object.freeze([
  {
    path: '/',
    moduleId: 'dashboard',
    page: LANDING_PAGE,
    title: 'Dashboard Overview',
    description: 'A consolidated view of your business performance across all modules.',
  },

  // Platform Core surfaces, which belong to the modules that own them.
  { path: '/tasks', moduleId: 'dashboard', page: 'My tasks' },
  { path: '/tasks/', moduleId: 'dashboard', page: 'Task', sidebar: false },
  { path: '/approvals', moduleId: 'dashboard', page: 'Approvals' },
  { path: '/approvals/', moduleId: 'dashboard', page: 'Approval', sidebar: false },
  { path: '/search', moduleId: 'dashboard', page: 'Search', sidebar: false },
  { path: '/search/saved', moduleId: 'dashboard', page: 'Saved searches', sidebar: false },

  { path: '/reports', moduleId: 'reports', page: LANDING_PAGE },
  { path: '/reports/new', moduleId: 'reports', page: 'New report', sidebar: false },
  { path: '/reports/', moduleId: 'reports', page: 'Report', sidebar: false },
  { path: '/exports', moduleId: 'reports', page: 'Exports' },
  { path: '/metrics', moduleId: 'reports', page: 'Metrics' },

  { path: '/parties', moduleId: 'administration', page: 'Parties' },
  { path: '/catalogue', moduleId: 'administration', page: 'Catalogue' },

  // Finance — the first module whose sidebar says what it actually contains.
  /*
   * THE MODULE'S OWN FRONT PAGE NAMES ITSELF.
   *
   * Without a title the `<h1>` falls back to the page's name, so Screen 01
   * announced itself as "Overview" under the breadcrumb "Finance › Overview" —
   * the module's name said once, in the smaller of the two lines. The frozen
   * Stitch reference heads the page "Finance Overview" and subtitles it with
   * the line below, and both belong in the route table for the reason the
   * table exists: the breadcrumb, the sidebar, the heading and the subtitle
   * then cannot disagree about what page this is.
   *
   * SAME SHAPE AS THE DASHBOARD'S OWN ENTRY, which reads "Dashboard Overview"
   * over "Dashboard › Overview" and is frozen. Nothing about that entry moves.
   */
  {
    path: '/finance',
    moduleId: 'finance',
    page: LANDING_PAGE,
    title: 'Finance Overview',
    description:
      'Manage your financial operations, monitor performance and access key finance functions.',
  },
  // Customers leads the sales group: an invoice is raised against one, so the
  // list of them is the first thing anybody selling needs.
  /*
   * THE SUBTITLES ARE STITCH'S OWN, and they live here rather than on the page.
   * Each of these screens draws its name and a line under it in its `code.html`
   * banner; the shell owns page identity, so the line comes from this table and
   * the body carries only what belongs to the record.
   */
  {
    path: '/finance/customers',
    moduleId: 'finance',
    page: 'Customers',
    permission: 'finance.customer.read',
    description: 'Manage client accounts, credit terms, receivables and contact details.',
  },
  /*
   * A CUSTOMER'S OWN PAGE, OUT OF THE SIDEBAR. It is reached from the customers
   * list, so it has no place in the navigation — but it needs its identity in
   * this table like every other page, because the shell is the page-identity
   * authority and a route missing from here renders an unnamed screen.
   */
  {
    path: '/finance/customers/',
    moduleId: 'finance',
    page: 'Customer',
    sidebar: false,
    description: 'What this customer has been billed, what they still owe, and where invoices go.',
  },
  {
    path: '/finance/invoices',
    moduleId: 'finance',
    page: 'Sales invoices',
    permission: 'finance.invoice.read',
    description: 'Create, monitor and manage customer billing and receivables.',
  },
  {
    path: '/finance/invoices/new',
    moduleId: 'finance',
    page: 'New sales invoice',
    sidebar: false,
    description: 'Raise a draft. Nothing is numbered, taxed or posted until you issue it.',
  },
  {
    path: '/finance/invoices/',
    moduleId: 'finance',
    page: 'Sales invoice',
    sidebar: false,
    description: 'What was billed, what it was posted as, and where the document went.',
  },
  /*
   * THE DOCUMENT AND THE SEND, both under the invoice they belong to and both
   * out of the sidebar: they are steps in issuing one invoice, not places.
   */
  {
    path: '/finance/invoices/*/document',
    moduleId: 'finance',
    page: 'Invoice document',
    sidebar: false,
    description: 'The invoice exactly as the customer receives it, rendered from the ledger.',
  },
  {
    path: '/finance/invoices/*/send',
    moduleId: 'finance',
    page: 'Send invoice',
    sidebar: false,
    description: 'Email the issued invoice to the customer, with the document attached.',
  },
  {
    path: '/finance/deliveries',
    moduleId: 'finance',
    page: 'Delivery history',
    permission: 'finance.invoice.read',
    /*
     * SHORTER, AND STILL SAYING THE THING IT MUST SAY. The header clamps a
     * subtitle to two lines and the previous one ran to three, so the clause
     * that stops the page overclaiming — that acceptance is not receipt — was
     * the part being ellipsised away. It leads now instead.
     */
    description:
      'Every send out of this entity. Accepted by a mail server is not that anybody received it.',
  },
  {
    path: '/finance/credit-notes',
    moduleId: 'finance',
    page: 'Credit notes',
    permission: 'finance.invoice.read',
  },
  {
    path: '/finance/receipts',
    moduleId: 'finance',
    page: 'Receipts',
    permission: 'finance.invoice.read',
  },
  {
    path: '/finance/receivables',
    moduleId: 'finance',
    page: 'Receivables ageing',
    permission: 'finance.invoice.read',
    description:
      'Outstanding customer receivables, grouped by how far past their due date they are.',
  },
  {
    path: '/finance/suppliers',
    moduleId: 'finance',
    page: 'Suppliers',
    permission: 'finance.supplier.read',
  },
  {
    path: '/finance/bills',
    moduleId: 'finance',
    page: 'Supplier bills',
    permission: 'finance.bill.read',
  },
  { path: '/finance/bills/new', moduleId: 'finance', page: 'New supplier bill', sidebar: false },
  { path: '/finance/bills/', moduleId: 'finance', page: 'Supplier bill', sidebar: false },
  {
    path: '/finance/supplier-credits',
    moduleId: 'finance',
    page: 'Supplier credits',
    permission: 'finance.supplier_credit.read',
  },
  {
    path: '/finance/payments',
    moduleId: 'finance',
    page: 'Payments',
    permission: 'finance.bill.read',
  },
  {
    path: '/finance/payables',
    moduleId: 'finance',
    page: 'Payables ageing',
    permission: 'finance.bill.read',
  },
  {
    path: '/finance/bank-accounts',
    moduleId: 'finance',
    page: 'Bank accounts',
    permission: 'finance.bank.read',
  },
  {
    path: '/finance/bank-accounts/',
    moduleId: 'finance',
    page: 'Bank account',
    sidebar: false,
  },
  {
    path: '/finance/match-rules',
    moduleId: 'finance',
    page: 'Matching rules',
    permission: 'finance.match_rule.read',
  },
  {
    path: '/finance/journals',
    moduleId: 'finance',
    page: 'Journals',
    permission: 'finance.journal.read',
  },
  { path: '/finance/journals/', moduleId: 'finance', page: 'Journal', sidebar: false },
  {
    path: '/finance/ledger',
    moduleId: 'finance',
    page: 'General ledger',
    permission: 'finance.journal.read',
  },
  {
    path: '/finance/trial-balance',
    moduleId: 'finance',
    page: 'Trial balance',
    permission: 'finance.report.read',
  },
  {
    path: '/finance/accounts',
    moduleId: 'finance',
    page: 'Chart of accounts',
    permission: 'finance.account.read',
  },
  {
    path: '/finance/recurring',
    moduleId: 'finance',
    page: 'Recurring journals',
    permission: 'finance.recurring.read',
  },
  {
    path: '/finance/revaluations',
    moduleId: 'finance',
    page: 'Revaluations',
    permission: 'finance.revaluation.read',
  },
  { path: '/finance/vat', moduleId: 'finance', page: 'VAT', permission: 'finance.vat.read' },
  { path: '/finance/vat/', moduleId: 'finance', page: 'VAT return', sidebar: false },
  {
    path: '/finance/reports',
    moduleId: 'finance',
    page: 'Finance reports',
    permission: 'finance.report.read',
  },
  {
    path: '/finance/calendar',
    moduleId: 'finance',
    page: 'Financial years',
    permission: 'finance.account.read',
  },
  {
    path: '/finance/setup',
    moduleId: 'finance',
    page: 'Finance settings',
    permission: 'finance.setup.manage',
  },
  { path: '/finance/coding', moduleId: 'finance', page: 'Coding', sidebar: false },
  { path: '/finance/item-defaults', moduleId: 'finance', page: 'Item defaults', sidebar: false },
]);

/**
 * The module and page a path belongs to.
 *
 * LONGEST MATCH WINS, so /finance/invoices/new is the create form rather than
 * the list, and /finance/invoices/$id falls to the `'/finance/invoices/'`
 * prefix rather than to `/finance`.
 */
export function pageIdentityFor(pathname: string): {
  moduleId: string;
  page: string;
  description?: string | undefined;
  title?: string | undefined;
} {
  const moduleMatch = /^\/m\/([^/]+)/.exec(pathname);
  if (moduleMatch?.[1] !== undefined) {
    return { moduleId: moduleMatch[1], page: LANDING_PAGE };
  }

  let best: PageEntry | undefined;
  for (const entry of PAGE_MAP) {
    /*
     * THE ROOT IS AN EXACT MATCH, and it had to be said out loud.
     *
     * A path ending in `/` is a parameterised route's prefix — `/finance/bills/`
     * matches `/finance/bills/$id`. But `'/'` ends in `/` too, so by that rule
     * it prefix-matched EVERY path in the product. Longest-match hid it:
     * `/finance/invoices` beat `/` and answered correctly, so nothing looked
     * wrong. What it did was give every UNKNOWN path the Main Dashboard's
     * identity — and, once the dashboard gained a description, the Main
     * Dashboard's description on a page that does not exist.
     */
    const matches =
      entry.path === '/'
        ? pathname === '/'
        : entry.path.includes('/*/')
          ? matchesWildcard(entry.path, pathname)
          : entry.path.endsWith('/')
            ? pathname.startsWith(entry.path)
            : pathname === entry.path;
    if (!matches) continue;
    if (best === undefined || entry.path.length > best.path.length) best = entry;
  }
  return best === undefined
    ? { moduleId: 'dashboard', page: LANDING_PAGE }
    : {
        moduleId: best.moduleId,
        page: best.page,
        description: best.description,
        title: best.title,
      };
}

/**
 * A `*` IN A ROUTE PATH MATCHES ONE SEGMENT, and nothing else.
 *
 * WHY THIS EXISTS. A trailing `/` prefix names a page whose id is its LAST
 * segment — `/finance/invoices/` for `/finance/invoices/$invoiceId`. It cannot
 * name a page BELOW a parameter, and `/finance/invoices/$invoiceId/document` is
 * exactly that: an id in the middle. Before this, the only way to give such a
 * page its own identity was a route-specific header, which is the thing the
 * shell exists to prevent.
 *
 * SEGMENT-WISE, NOT A REGULAR EXPRESSION. `*` matches one non-empty segment and
 * the counts must agree, so `/finance/invoices/*\/document` names the document
 * page of one invoice and cannot accidentally claim a deeper path.
 */
function matchesWildcard(pattern: string, pathname: string): boolean {
  const wanted = pattern.split('/');
  const actual = pathname.split('/');
  if (wanted.length !== actual.length) return false;
  return wanted.every((segment, index) =>
    segment === '*' ? (actual[index] ?? '') !== '' : segment === actual[index],
  );
}

/** The path a module/page pair navigates to, for the sidebar's own rows. */
export function pathFor(moduleId: string, page: string): string {
  const entry = PAGE_MAP.find(
    (candidate) => candidate.moduleId === moduleId && candidate.page === page,
  );
  return entry?.path ?? `/m/${moduleId}`;
}

/**
 * The sidebar, with each module's real pages.
 *
 * A page appears only where the session holds the permission its route and its
 * API both require — the same COURTESY the Finance landing cards already
 * applied, now where navigation belongs. It is not the control: the route
 * guard refuses the same URL typed by hand, and the server refuses it again.
 */
export function navigationFor(session: SessionState): ShellModule[] {
  const reachable = reachableNavigationModules(session);
  const held = new Set(session.authenticated ? session.permissions : []);
  return FROZEN_NAVIGATION.filter((entry) => reachable.has(entry.id)).map((entry) => {
    const pages = PAGE_MAP.filter(
      (candidate) =>
        candidate.moduleId === entry.id &&
        candidate.sidebar !== false &&
        (candidate.permission === undefined || held.has(candidate.permission)),
    ).map((candidate) => candidate.page);
    return {
      id: entry.id,
      label: entry.label,
      icon: entry.icon,
      pages: pages.length > 0 ? pages : [LANDING_PAGE],
    };
  });
}
